<template>
  <div
    class="bg-white rounded-lg shadow-sm border border-gray-200 py-4 px-4 mb-4"
  >
    <!-- Header -->
    <div class="mb-2 flex items-center justify-between gap-2">
      <h3 class="text-sm font-semibold text-black">Filtrer la liste</h3>
      <button
        type="button"
        class="flex items-center gap-1 text-xs font-medium text-black px-2 py-1 border border-gray-300 rounded md:hidden"
        @click="isCollapsed = !isCollapsed"
      >
        <i
          :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
        ></i>
        {{ isCollapsed ? "Afficher" : "Masquer" }}
      </button>
    </div>

    <!-- Content collapsible on mobile -->
    <div v-show="!isCollapsed">
      <form
        @submit.prevent="handleSubmit"
        @submit.stop
        class="space-y-2 pt-2"
        novalidate
        autocomplete="off"
      >
        <!-- Première ligne de filtres -->
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <!-- Numéro de commande -->
          <div>
            <label
              for="orderId"
              class="block text-xs font-medium text-black mb-0.5"
            >
              N° commande
            </label>
            <input
              id="orderId"
              v-model.trim="filters.orderId"
              type="text"
              placeholder="N° commande"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <!-- Statut -->
          <div>
            <label
              for="status"
              class="block text-xs font-medium text-black mb-0.5"
            >
              Statut
            </label>
            <select
              id="status"
              v-model="filters.status"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Tous</option>
              <option value="1">En attente</option>
              <option value="2">Expédiée</option>
              <option value="3">Annulée</option>
              <option value="4">Confirmée</option>
              <option value="14">Reçu au dépôt</option>
            </select>
          </div>

          <!-- Client -->
          <div>
            <label
              for="buyer"
              class="block text-xs font-medium text-black mb-0.5"
            >
              Client
            </label>
            <input
              id="buyer"
              v-model.trim="filters.buyer"
              type="text"
              placeholder="Nom client"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <!-- Date de commande -->
          <div>
            <label class="block text-xs font-medium text-black mb-0.5">
              Date cmd
            </label>
            <input
              v-model="filters.orderDate"
              type="date"
              :max="maxDate"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <!-- Date d'expédition -->
          <div>
            <label class="block text-xs font-medium text-black mb-0.5">
              Date exp.
            </label>
            <input
              v-model="filters.shippingDate"
              type="date"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <!-- Transporteur -->
          <div>
            <label
              for="carrier"
              class="block text-xs font-medium text-black mb-0.5"
            >
              Transporteur
            </label>
            <select
              id="carrier"
              v-model="filters.carrier"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Sélection transporteur --</option>
              <optgroup label="Transporteur">
                <option value="colissimo">Colissimo</option>
                <option value="dpd">DPD</option>
                <option value="ups">UPS</option>
                <option value="gls">GLS</option>
                <option value="chronopost">Chronopost</option>
                <option value="dhl">DHL</option>
                <option value="fedex">Fedex</option>
                <option value="bollore">Bolloré</option>
                <option value="transportAcheteur">Transporteur Acheteur</option>
              </optgroup>
              <optgroup label="Personaliser">
                <option value="UPSgroupage">UPS ( Groupage )</option>
                <option value="UPSstandardGroupage">
                  UPS Standard ( Groupage )
                </option>
                <option value="FedexGroupage">Fedex ( Groupage )</option>
                <option value="ChronopostGroupage">
                  Chrono Multi ( Groupage )
                </option>
                <option value="GLSmulti">GLS ( Multi )</option>
                <option value="gpx">GPX ( Groupage )</option>
                <option value="ocx">OCX ( Groupage )</option>
              </optgroup>
            </select>
          </div>

          <!-- Pays -->
          <div>
            <label
              for="origin"
              class="block text-xs font-medium text-black mb-0.5"
            >
              Pays
            </label>
            <select
              id="origin"
              v-model="filters.origin"
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              :disabled="isLoadingCountries"
            >
              <option value="">Tous</option>
              <option
                v-for="country in countries"
                :key="country.id_pays"
                :value="country.id_pays.toString()"
              >
                {{ country.texte_fr }}
              </option>
            </select>
          </div>
        </div>

        <!-- Bouton filtrer -->
        <!-- <div class="flex justify-end">
          <button 
            type="submit"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <i class="fas fa-filter mr-2"></i>
            Filtrer
          </button>
        </div> -->

        <!-- Bouton réinitialiser  -->
        <div class="flex justify-end pt-1 pb-2">
          <button
            v-if="hasActiveFilters"
            type="button"
            @click="handleReset"
            class="inline-flex items-center px-3.5 py-1 text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500"
          >
            <i class="fas fa-undo mr-1 text-xs"></i>
            Réinitialiser
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import type { OrderFilters } from "@/types/order";
import { getCountriesFromOrders } from "@/api/orders";

interface Props {
  initialFilters?: OrderFilters;
}

const props = withDefaults(defineProps<Props>(), {
  initialFilters: () => ({
    status: "",
    orderId: "",
    carrier: "",
    orderDate: "",
    shippingDate: "",
    buyer: "",
    origin: "",
  }),
});

interface Emits {
  (e: "filters", filters: OrderFilters): void;
}

const emit = defineEmits<Emits>();

// Default empty filters for reset
const defaultFilters: OrderFilters = {
  status: "",
  orderId: "",
  carrier: "",
  orderDate: "",
  shippingDate: "",
  buyer: "",
  origin: "",
};

const filters = ref<OrderFilters>({ ...props.initialFilters });
const isCollapsed = ref(true);

// Appliquer les filtres initiaux quand ils changent (sans émettre immédiatement)
watch(
  () => props.initialFilters,
  (newFilters) => {
    if (newFilters) {
      filters.value = { ...newFilters };
    }
  },
  { immediate: true },
);

// Watch sur les filtres pour émettre automatiquement avec debounce
let isInitialized = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastEmittedFilters: OrderFilters | null = null;

watch(
  () => filters.value,
  (newFilters, oldFilters) => {
    // Ne pas émettre si le composant n'est pas encore initialisé
    if (!isInitialized) return;

    // Vérifier si les filtres ont vraiment changé
    const filtersChanged =
      !lastEmittedFilters ||
      JSON.stringify(newFilters) !== JSON.stringify(lastEmittedFilters);

    if (!filtersChanged) return;

    // Annuler le timer précédent s'il existe
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Attendre 100 avant d'émettre les filtres
    debounceTimer = setTimeout(() => {
      if (newFilters && typeof newFilters === "object") {
        lastEmittedFilters = { ...newFilters };
        emit("filters", { ...newFilters });
      }
    }, 100);
  },
  { deep: true },
);

const maxDate = computed(() => {
  const today = new Date();
  return today.toISOString().split("T")[0];
});

// Computed property to check if any filter is active
const hasActiveFilters = computed(() => {
  if (!filters.value || typeof filters.value !== "object") {
    return false;
  }
  return Object.values(filters.value).some(
    (value) => value !== null && value !== undefined && value !== "",
  );
});

const countries = ref<Array<{ id_pays: number; texte_fr: string }>>([]);
const isLoadingCountries = ref(false);

const handleSubmit = (event?: Event) => {
  // Empêcher le comportement par défaut du formulaire si l'événement est fourni
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // S'assurer que filters.value existe et est un objet
  if (!filters.value || typeof filters.value !== "object") {
    filters.value = { ...defaultFilters };
  }

  emit("filters", { ...filters.value });
};

const handleReset = () => {
  filters.value = { ...defaultFilters };
  emit("filters", { ...defaultFilters });
};

// Charger les pays depuis la base de données
const loadCountries = async () => {
  try {
    isLoadingCountries.value = true;
    const pays = await getCountriesFromOrders();
    countries.value = pays;
  } catch (error) {
    // En cas d'erreur, on garde un tableau vide
    countries.value = [];
  } finally {
    isLoadingCountries.value = false;
  }
};

onMounted(() => {
  // Charger les pays depuis la DB
  loadCountries();

  // Marquer comme initialisé après un court délai pour éviter l'émission initiale
  setTimeout(() => {
    isInitialized = true;
  }, 100);

  // Ouvrir par défaut sur desktop, fermer sur mobile
  const handleResize = () => {
    isCollapsed.value = window.innerWidth < 768;
  };
  handleResize();
  window.addEventListener("resize", handleResize);
  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });
});

onUnmounted(() => {
  // Cleanup debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
});
</script>
